import { RowFieldWrites } from "./row-field-writes.ts";
import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";
import type { RowNode, Resolved } from "./row-node.ts";
import { cellAccessKey } from "../sync/cell-access-key.ts";
import { placeInTree, rowFacadeOf, RowMirror, type SheetLifecycle, SheetRow, SheetRowContext } from "./sheet-row.ts";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import { SheetRowEdge } from "./sheet-row-edge.ts";
import { RootNode } from "./sheet-row-edge.ts";
import { RowLocalId, RowNetworkKey, UserId } from "@tento-lona/sheets";
import { AliasRow, BodyViewTree, type RowSection } from "@tento-lona/sheets/platform";
import { FormulaEngine } from "@tento-lona/sheets/platform";
import { wireTypeOf } from "@tento-lona/sheets/platform";
import { flatten as flattenRowsSideTree, InertRow, RendererRow } from "@tento-lona/sheets/platform";
import type { TypedApi } from "@tento-core/typed-api";
import type { CellAccessor } from "../cells/sheet-cell-accessor.ts";
import type { Network } from "../network.ts";
import type { SheetHost } from "../client/sheet-host.ts";
import type { Timescale } from "../view-model/view-model.ts";
import { CellLayout } from "@tento-core/sheet-layout/cell-layout";
import { SheetLayoutSpec } from "@tento-core/sheet-layout/sheet-layout-spec";
import { LiveData } from "@tento-core/livedata";
import type { OrderedTree } from "@tento-core/ordered-tree";
import { Rational } from "@tento-core/rational";
import { Actor, type InvalidationSink, LayoutChanged } from "../invalidation.ts";
import type { RowHeightPreferences } from "../preferences/row-height-preferences.ts";
import type { EventsTable } from "../indices/events-table.ts";
import type { ModelRow } from "@tento-lona/sheets";
import type { Sheet } from "@tento-lona/sheets/platform";

export { RootNode };

type Node = OrderedTree.Node<RowLocalId, Rational>;

type JoinedFlattenResult = {
  readonly rows: readonly SheetRow.Joined.Handle[];
  readonly subsumed: ReadonlySet<RowLocalId>;
  readonly rendersChildren: ReadonlyMap<RowLocalId, boolean>;
};

function inlineTransparentDataAliases(
  views: readonly BodyViewTree.View<ModelRow>[],
): BodyViewTree.View<ModelRow>[] {
  const out: BodyViewTree.View<ModelRow>[] = [];
  for (const view of views) {
    const children = view.children();
    if (isTransparentDataAlias(view.row)) {
      out.push(...inlineTransparentDataAliases(children));
      continue;
    }
    let cachedChildren: Option<BodyViewTree.View<ModelRow>[]> = null;
    out.push({
      row: view.row,
      children: () =>
        cachedChildren ??= inlineTransparentDataAliases(children),
    });
  }
  return out;
}

// A "data" alias is fully transparent in flatten — inlined so its single
// data-source child renders in its place with no wrapper parent row.
function isTransparentDataAlias(row: ModelRow): boolean {
  return row instanceof AliasRow && row.descriptor.template === "data";
}

/** Set once, by `SheetRowTree`'s static block: `treeCellAccessorOf`'s read. */
let cellAccessorOfTree: (tree: SheetRowTree) => Option<CellAccessor>;
/** Set once, by `SheetRowTree.CellWiring`'s static block. */
let wiringOf: (accessor: CellAccessor) => SheetRowTree.CellWiring;
/** Set once, by `SheetRowTree.CellWiring`'s static block. */
let accessorOfWiring: (wiring: SheetRowTree.CellWiring) => Option<CellAccessor>;

/**
 * The cell accessor `tree` was built with (`SheetRowTree.Config.cells`), or
 * `null` for a tree without one. The SDK's friend door: the formula
 * evaluator reads a row's cells through it. No barrel re-exports it — the
 * accessor's members address rows by raw id strings, which the published
 * `SheetRowTree` does not carry.
 */
export function treeCellAccessorOf(tree: SheetRowTree): Option<CellAccessor> {
  return cellAccessorOfTree(tree);
}

/**
 * `accessor` as a tree's opaque cell wiring (`SheetRowTree.Config.cells`).
 * The SDK's friend door, like `treeCellAccessorOf`: no barrel re-exports it.
 */
export function treeCellWiring(accessor: CellAccessor): SheetRowTree.CellWiring {
  return wiringOf(accessor);
}

/**
 * A sheet's rows as the SDK renders them: the resolved row tree and its
 * system rows, built from a handle's model.
 */
export class SheetRowTree {
  static readonly ACTOR = new Actor("sheet-row-tree");
  readonly #inner: RowNode.Tree<typeof Resolved>;

  static {
    cellAccessorOfTree = (tree) => tree.#cellAccessor;
  }

  /**
   * The context this tree's row facades answer through: the sheet's owner
   * (`Sheet.facadeOwner`, the SDK's handle over it), else — a tree no handle
   * holds (a headless test tree) — one built from the tree's own config and
   * registered as the sheet's owner.
   */
  #facadeContext(config: SheetRowTree.Config): SheetRowContext {
    const owner = config.rowsSheet.facadeOwner;
    if (owner instanceof SheetRowContext) return owner;
    const context = new SheetRowContext({
      api: config.api,
      host: config.host,
      invalidation: config.invalidation,
      mirror: config.rowMirror ?? RowMirror.NONE,
      writes: config.rowWrites ?? RowFieldWrites.ofSheet(config.rowsSheet),
      rowsSheet: config.rowsSheet,
      lifecycle: config.sheetLifecycle ?? null,
      cells: this.#cellAccessor,
      eventsTable: config.eventsTable ?? null,
      rowHeightPrefs: config.rowHeightPreferences ?? null,
      editable: () => config.editable ?? false,
      positionOf: () => null,
      backing: () => null,
    });
    config.rowsSheet.ownFacades(context);
    return context;
  }

  /** The formula's unambiguous cell-bearing row, resolved by the owning model's name snapshot. */
  formulaRow(name: string): Option<SheetRow.Joined.Handle> {
    const row = FormulaEngine.resolveReference(this.#rowsSheet, name);
    return row == null ? null : this.byId.get(row.id) ?? null;
  }

  // ── Config ──
  readonly #cellAccessor: Option<CellAccessor> = null;
  #systemRowIds: Set<RowLocalId>;
  #footerRowIds: Set<RowLocalId>;
  readonly #idRegistry: RowLocalId.Registry;
  readonly timescale: Timescale;
  readonly #host: SheetHost;
  readonly #invalidation: InvalidationSink;
  readonly #rowsSheet: Sheet;

  // ── Eagerly built state ──
  readonly byId: ReadonlyMap<RowLocalId, SheetRow.Joined.Handle>;
  readonly childrenIds: ReadonlyMap<RowLocalId | RootNode, RowLocalId[]>;
  readonly parentMap: ReadonlyMap<RowLocalId, RowLocalId | RootNode>;
  readonly topLevelRows: SheetRow.Joined.Handle[];
  readonly lookupKeyToId: ReadonlyMap<string, RowLocalId>;
  readonly labelToId: ReadonlyMap<string, RowLocalId>;
  flattened: {
    rows: SheetRow.Joined.Handle[];
    wireTypes: string[];
    nonSystemRows: SheetRow.Joined.Handle[];
  };
  readonly numFrozenRowsSource: {
    numFrozen: number;
    numFrozenFlattened: number;
  };
  readonly numFooterRowsSource: {
    numFooter: number;
    numFooterFlattened: number;
  };
  readonly numSystemRows: number;

  // ── Layout state ──
  readonly cache: { layout: Option<SheetLayoutSpec<RowLocalId>> } = {
    layout: null,
  };
  readonly #onInvalidate = new LiveData(null);
  readonly #flatIndexById = new Map<RowLocalId, number>();
  #previousRendersChildrenState: Map<RowLocalId, boolean> = new Map();

  constructor(
    inner: RowNode.Tree<typeof Resolved>,
    config: SheetRowTree.Config,
  ) {
    this.#inner = inner;
    this.#systemRowIds = config.systemRowIds;
    this.#footerRowIds = config.footerRowIds;
    this.#idRegistry = config.idRegistry;
    this.timescale = config.timescale;
    this.#host = config.host;
    this.#invalidation = config.invalidation;
    this.#cellAccessor = config.cells == null ? null : accessorOfWiring(config.cells);
    this.#rowsSheet = config.rowsSheet;

    // Every row of the tree is its sheet's ONE facade (`Sheet.facadeFor`),
    // minted and answered by the sheet's owner — the SDK's handle over it —
    // else, for a tree no handle holds, by a context of the tree's own.
    const context = this.#facadeContext(config);

    // Build joined rows and structural maps
    const byId = new Map<RowLocalId, SheetRow.Joined.Handle>();
    const childrenIdsMap = new Map<RowLocalId | RootNode, RowLocalId[]>();
    const parentMapLocal = new Map<RowLocalId, RowLocalId | RootNode>();
    const lookupKeyToId = new Map<string, RowLocalId>();
    const labelToId = new Map<string, RowLocalId>();

    const registerJoined = (
      rowsRow: ModelRow,
      sheetRow: SheetRow,
      id: RowLocalId,
      position: Rational,
      parentId: RowLocalId | RootNode,
      isRendererProjection: boolean,
    ): void => {
      let rowHandle: Option<RowCellCache> = null;
      if (this.#cellAccessor) {
        const rowKey = cellAccessRowKey(sheetRow);
        rowHandle = this.#cellAccessor.rowHandle(RowNetworkKey.fromWire(rowKey)) ?? null;
        // A renderer reads its data row (`RendererRow.dataRow`): its container,
        // or the address its own config names when a section or inert chrome
        // contains it — one binding either way.
        const dataKey = isRendererProjection ? rendererDataKey(rowsRow, parentId) : null;
        if (dataKey != null) {
          this.#cellAccessor.bindRendererToSource(RowNetworkKey.fromWire(rowKey), dataKey);
        }
      }
      const base = new SheetRow.Joined(
        sheetRow,
        new SheetRowEdge(id, position, parentId, this),
        this.#idRegistry,
        { rowHandle, systemRowIds: this.#systemRowIds, footerRowIds: this.#footerRowIds },
      );
      // The row's one facade (the same object across rebuilds), placed in
      // this tree: its tree reads answer from `base` from now on.
      context.bridge(sheetRow);
      const joined = rowFacadeOf(this.#rowsSheet, id);
      placeInTree(joined, base);

      byId.set(id, joined);
      parentMapLocal.set(id, parentId);
      if (sheetRow.lookupKey) lookupKeyToId.set(sheetRow.lookupKey, id);
      if (sheetRow.label) labelToId.set(sheetRow.label, id);
    };

    // The rows-side sheet is the canonical hydrated tree. Body and
    // inspection surfaces share this full structure; the body/legend
    // projection is applied later by flatten/layout, so plugins can
    // still discover hydrated implementation descendants through
    // row.children().
    const rowsSheet = this.#rowsSheet;
    // The rows-side tree, read through the Sheet's named verbs (its stores
    // are private to `@tento/lona/sheets`).
    const rowsTree = {
      parent: (id: RowLocalId) => rowsSheet.parentOf(id),
      roots: () => rowsSheet.rootIds(),
      children: (id: RowLocalId) => rowsSheet.childrenOf(id),
    };
    const allRows = rowsSheet.allRows();
    const innerRows = rowsByIdString(inner);
    const innerPositions = positionsById(inner);
    for (const rowsRow of allRows) {
      const id = rowsRow.id;
      const idString = id.toString();
      const fromInner =
        inner.metadata.get(id) ?? innerRows.get(idString) ?? null;
      let sheetRow = fromInner;
      if (sheetRow == null) {
        sheetRow = bridgeRowsSideRowToSheetRow(rowsRow);
        // Bridged rows bypass `SheetRow.Registry.set`, so plugin
        // descriptor overrides (dynamic heights, labels) must be
        // applied here — e.g. the calendar template's hydrated
        // `timeline-row` strip child.
        config.descriptors?.apply(sheetRow);
      } else {
        mergeRowsSideMetadata(sheetRow, rowsRow);
      }
      const parentRowsId = rowsTree.parent(id);
      const parentId: RowLocalId | RootNode =
        parentRowsId == null ? RootNode : parentRowsId;
      const siblings = parentRowsId == null
        ? rowsTree.roots()
        : rowsTree.children(parentRowsId);
      const indexInParent = siblings.indexOf(id);
      const fallbackPosition = Rational.nth(Math.max(indexInParent, 0));
      const position = innerPositions.get(id) ?? fallbackPosition;
      registerJoined(
        rowsRow,
        sheetRow,
        id,
        position,
        parentId,
        rowsRow.descriptor.role === "renderer",
      );
    }
    for (const id of byId.keys()) {
      const childIds = orderRowsSideSiblings(
        rowsTree.children(id),
        innerPositions,
      );
      if (childIds.length > 0) childrenIdsMap.set(id, [...childIds]);
    }
    const rootIds = orderRowsSideSiblings(rowsTree.roots(), innerPositions);

    // Sort top-level into four tiers: header surface anchor first
    // (its subtree is the frozen flat-output prefix), then system
    // non-footer rows, then body, then footer. The sort is stable,
    // so relative authored order within a tier is preserved.
    const tierOf = (r: SheetRow.Joined.Handle): number => {
      if (r.surface === "header") return 0;
      if (r.surface === "footer") return 3;
      if (r.isSystemRow) return 1;
      return 2;
    };
    rootIds.sort((a, b) => {
      const ra = byId.get(a);
      const rb = byId.get(b);
      if (!ra || !rb) return 0;
      return tierOf(ra) - tierOf(rb);
    });
    childrenIdsMap.set(RootNode, rootIds);

    this.byId = byId;
    this.childrenIds = childrenIdsMap;
    this.parentMap = parentMapLocal;
    this.lookupKeyToId = lookupKeyToId;
    this.labelToId = labelToId;

    // Top-level rows
    this.topLevelRows = rootIds
      .map((id) => byId.get(id))
      .filter((r): r is SheetRow.Joined.Handle => r != null);

    // System rows count (non-footer system rows)
    this.numSystemRows = this.topLevelRows.filter(
      (r) => r.isSystemRow && r.surface !== "footer",
    ).length;

    // Frozen rows — structural surface accounting. The unique
    // header surface row's subtree IS the frozen surface.
    this.numFrozenRowsSource = this.calculateFrozenRows();

    // Footer rows
    this.numFooterRowsSource = this.#calculateFooterRows();

    // Build flattened
    this.flattened = { rows: [], wireTypes: [], nonSystemRows: [] };
    this.#buildFlattened();

    // Compute initial layout
    this.cache.layout = this.computeLayout();
  }

  // ── Row access ──────────────────────────────────────────────────

  get(id: RowLocalId): Option<SheetRow.Joined.Handle> {
    return this.byId.get(id) ?? null;
  }

  get rows(): Iterable<SheetRow.Joined.Handle> {
    return this.byId.values();
  }

  children(parentId: RowLocalId | RootNode): SheetRow.Joined.Handle[] {
    const childIds = this.childrenIds.get(parentId);
    if (!childIds) return [];
    const result: SheetRow.Joined.Handle[] = [];
    for (const cid of childIds) {
      const row = this.byId.get(cid);
      if (row) result.push(row);
    }
    return result;
  }

  /**
   * Body-view-tree-aware children. Same shape as `children()` but
   * uses the projection-hoisted view tree so consumers (legend,
   * binder, anything that walks the rendered structure) see the
   * augmented children of surface-container rows, including
   * projection content hoisted from a calendar template's
   * `projection [surface=header]` child.
   *
   * Returns authored children for rows that aren't a hoist target.
   * `null` parent → top-level rows from the view tree.
   */
  viewChildren(parentId: Option<RowLocalId>): SheetRow.Joined.Handle[] {
    const view = this.#deriveBodyViewTreeJoined();
    return parentId == null
      ? view.topLevelJoined
      : view.childrenById(parentId);
  }

  parent(id: RowLocalId): Option<SheetRow.Joined.Handle> {
    const parentId = this.parentMap.get(id);
    if (!parentId || typeof parentId === "symbol") return null;
    return this.byId.get(parentId) ?? null;
  }

  get roots(): SheetRow.Joined.Handle[] {
    return this.topLevelRows;
  }

  // ── Frozen/footer ─────────────────────────────────────────────

  /**
   * Structural surface accounting (Phase E1). The frozen surface
   * is the unique header row's subtree — synthetic placeholder
   * (`InertRow + kind === "header"`) or server-backed
   * (`meta.surface === "header"`); the header row itself plus
   * every flat-output descendant counts.
   *
   * The legacy `numFrozenRows`-driven accounting retired with
   * Phase C2 — the production migration reparented every
   * previously-frozen row under a server-backed header surface,
   * so the structural read produces the same answer for migrated
   * sheets and the mechanism's wire field is no longer load-
   * bearing. Sheets that still have `num_frozen_rows > 0` set in
   * the wire format (none in production after C2) will see those
   * rows render as body — accepted regression.
   */
  calculateFrozenRows(): {
    numFrozen: number;
    numFrozenFlattened: number;
  } {
    if (this.#rowsSheet == null) return { numFrozen: 0, numFrozenFlattened: 0 };
    const header = this.#rowsSheet.peekHeaderRow();
    if (header == null) return { numFrozen: 0, numFrozenFlattened: 0 };
    // Walk the header row's subtree via the body view tree's
    // children resolver — same shape `flatten` consumes, so
    // membership and flat-output filter agree by construction.
    const view = this.#deriveBodyViewTreeRowsSide(
      this.#rowsSheet,
      this.topLevelRows,
    );
    const frozenSubtreeIds = new Set<RowLocalId>();
    const collect = (row: ModelRow): void => {
      frozenSubtreeIds.add(row.id);
      for (const kid of view.childrenOfRowsSide(row)) collect(kid);
    };
    const headerRowSide = view.topLevelRowsSide.find((r) => r.id === header.id);
    if (headerRowSide != null) collect(headerRowSide);
    const flatRows = this.#flattenRowsForBody(this.topLevelRows).rows;
    let numFrozenFlattened = 0;
    for (const row of flatRows) {
      if (frozenSubtreeIds.has(row.id)) numFrozenFlattened += 1;
    }
    return { numFrozen: 1, numFrozenFlattened };
  }

  getRowLocation(rowId: RowLocalId): Option<RowSection> {
    const numFrozen = this.layout.numFrozenRowsLayout.numFrozenFlattened;
    const numFooter = this.layout.numFooterRowsLayout.numFooterFlattened;
    const rows = this.flattened.rows;
    const rowIndex = this.#flatIndexById.get(rowId);
    if (rowIndex == null) return null;

    if (rowIndex < numFrozen) return "header";
    if (rowIndex >= rows.length - numFooter) return "footer";
    return "body";
  }

  /**
   * Whether `rid` is in the currently visible sticky header region
   * — i.e. in the header surface subtree AND inside the F3
   * viewport cap (not spilled into body). Use this for per-row UI
   * questions like "is this row pinned at the top of the viewport?"
   * (legend pinning, row-options-anchor stickiness, etc).
   *
   * Replaces `isFrozen(id, Frozen.Layout)`. For pure structural
   * membership ("is this row authored under the header surface,
   * regardless of viewport-cap?"), use `row.surface === "header"`.
   */
  isInVisibleHeader(rid: RowLocalId): boolean {
    const idx = this.#flatIndexById.get(rid);
    if (idx == null) return false;
    return idx < this.layout.numFrozenRowsLayout.numFrozenFlattened;
  }

  // ── Layout ────────────────────────────────────────────────────

  /** Clear the cached layout so the next .layout access recomputes. */
  clearLayoutCache(): void {
    this.cache.layout = null;
  }

  get layout(): SheetLayoutSpec<RowLocalId> {
    if (this.cache.layout) return this.cache.layout;
    return (this.cache.layout = this.computeLayout());
  }

  computeLayout(): SheetLayoutSpec<RowLocalId> {
    for (const row of this.rows) row.joined.subsumed = false;
    this.#markRowsSideSubsumed();
    const maxHeight = this.#host.getMaxHeight();
    // Both flatten and cell-layout walk the body view tree so the
    // flat output and layout offsets agree by construction. The
    // view tree hoists projection children onto the dedicated
    // header / footer rows; consumers see the same shape both
    // walks operate on.
    const view = this.#deriveBodyViewTreeJoined();
    const { layout } = SheetLayoutSpec.make({
      cellLayout: CellLayout.Map.make(
        view.topLevelJoined,
        maxHeight,
        this.timescale,
        view.childrenOfJoined,
      ),
      timescale: this.timescale,
      maxHeight,
      numFrozenRowsSource: this.numFrozenRowsSource,
      numSystemRows: this.numSystemRows,
      calculateFrozenRows: () => this.calculateFrozenRows(),
      numFooterRowsSource: this.numFooterRowsSource,
      flattenedRows: this.flattened.rows,
    });
    return layout;
  }

  invalidateLayout(): void {
    const previous = this.cache.layout;

    const nextRendersChildrenState = this.#currentRendersChildrenState();
    let needsRebuild =
      nextRendersChildrenState.size !== this.#previousRendersChildrenState.size;
    for (const [id, currState] of nextRendersChildrenState) {
      const prevState = this.#previousRendersChildrenState.get(id);
      if (prevState === currState) continue;
      needsRebuild = true;
      break;
    }
    this.#previousRendersChildrenState = new Map(nextRendersChildrenState);

    if (needsRebuild) {
      this.rebuildFlattened();
    }

    const newLayout = this.computeLayout();
    if (previous?.equals(newLayout)) return;

    this.cache.layout = newLayout;
    this.#onInvalidate.update(null);
    this.#invalidation.emit(new LayoutChanged(SheetRowTree.ACTOR));
  }

  rebuildFlattened(): void {
    this.flattened = { rows: [], wireTypes: [], nonSystemRows: [] };
    this.#buildFlattened();
  }

  // ── Navigation ────────────────────────────────────────────────

  /** Return the row's cached flattened position without rebuilding the tree. */
  peekFlattenedRowIdx(row: { id: RowLocalId }): Option<number> {
    return this.#flatIndexById.get(row.id) ?? null;
  }

  get firstRow(): Option<SheetRow.Joined.Handle> {
    return this.flattened.rows[0] ?? null;
  }

  get lastRow(): Option<SheetRow.Joined.Handle> {
    const rows = this.flattened.rows;
    return rows[rows.length - 1] ?? null;
  }

  parentOf(row: SheetRow.Joined.Handle): Option<SheetRow.Joined.Handle> {
    const parentId = this.parentMap.get(row.id);
    if (!parentId || typeof parentId === "symbol") return null;
    return this.byId.get(parentId) ?? null;
  }

  peekByLabel(label: string): Option<SheetRow.Joined.Handle> {
    for (const row of this.rows) {
      if (row.label === label) return row;
    }
    return null;
  }

  isAncestorOf(ancestor: SheetRow.Joined.Handle, leaf: SheetRow.Joined.Handle): boolean {
    let curr: Option<SheetRow.Joined.Handle> = leaf;
    while (curr != null) {
      if (curr.id === ancestor.id) return true;
      curr = this.parentOf(curr);
    }
    return false;
  }

  siblingBefore(row: SheetRow.Joined.Handle): Option<SheetRow.Joined.Handle> {
    const parentId = this.parentMap.get(row.id);
    if (!parentId || typeof parentId === "symbol") {
      const childIdx = this.topLevelRows.findIndex((r) => r.id === row.id);
      return this.topLevelRows[childIdx - 1] ?? null;
    }
    const siblings = this.children(parentId);
    const childIdx = siblings.findIndex((r) => r.id === row.id);
    return siblings[childIdx - 1] ?? null;
  }

  siblingAfter(row: SheetRow.Joined.Handle): Option<SheetRow.Joined.Handle> {
    const parentId = this.parentMap.get(row.id);
    if (!parentId || typeof parentId === "symbol") {
      const childIdx = this.topLevelRows.findIndex((r) => r.id === row.id);
      return this.topLevelRows[childIdx + 1] ?? null;
    }
    const siblings = this.children(parentId);
    const childIdx = siblings.findIndex((r) => r.id === row.id);
    return siblings[childIdx + 1] ?? null;
  }

  // ── Debug ─────────────────────────────────────────────────────

  dbg(): void {
    Tracing.info("?")(this.print(null));
  }

  /** Pretty-print the tree using unicode box-drawing characters.
   *
   * Walks the rows-side canonical structure via `childrenIds` /
   * `byId` rather than the legacy `inner: RowNode.Tree` — the legacy
   * tree only carries network-shaped rows and would print a tree
   * shape that doesn't match what consumers see (it omits
   * synthesized virtual rows from alias hydration, dynamicTail, and
   * activeLayout).
   */
  print(
    label: Option<(row: SheetRow.Joined.Handle) => string>,
  ): string {
    const fmt = (joined: SheetRow.Joined.Handle) =>
      label ? label(joined) : `[${joined.descriptor.id}] ${joined.label ?? ""}`;
    const lines: string[] = [];
    const printNode = (
      id: RowLocalId,
      prefix: string,
      isLast: boolean,
      isRoot: boolean,
    ) => {
      const joined = this.byId.get(id);
      const text = joined ? fmt(joined) : id.toString();
      const connector = isRoot ? "" : isLast ? "└─ " : "├─ ";
      lines.push(prefix + connector + text);
      const childIds = this.childrenIds.get(id) ?? [];
      const childPrefix = isRoot ? "" : prefix + (isLast ? "   " : "│  ");
      childIds.forEach((childId, i) => printNode(childId, childPrefix, i === childIds.length - 1, false));
    };
    const rootIds = this.childrenIds.get(RootNode) ?? [];
    rootIds.forEach((rootId, i) => printNode(rootId, "", i === rootIds.length - 1, true));
    return lines.join("\n");
  }

  // ── Private ───────────────────────────────────────────────────

  #calculateFooterRows(): {
    numFooter: number;
    numFooterFlattened: number;
  } {
    let numFooter = 0;
    for (let i = this.topLevelRows.length - 1; i >= 0; i--) {
      if (this.topLevelRows[i]?.surface !== "footer") break;
      numFooter++;
    }
    let numFooterFlattened = 0;
    if (numFooter > 0 && this.#rowsSheet != null) {
      // Mirror calculateFrozenRows: collect the footer-subtree row
      // ids (using the view tree so projection-to-footer hoists
      // count) and then filter the flatten output by membership.
      // Filtering by flatten output respects leaf-alias subsumption
      // — counting raw view-tree nodes would over-count.
      const view = this.#deriveBodyViewTreeRowsSide(
        this.#rowsSheet,
        this.topLevelRows,
      );
      const footerSubtreeIds = new Set<RowLocalId>();
      const collect = (row: ModelRow): void => {
        footerSubtreeIds.add(row.id);
        for (const kid of view.childrenOfRowsSide(row)) collect(kid);
      };
      const startIdx = Math.max(
        0,
        view.topLevelRowsSide.length - numFooter,
      );
      for (const row of view.topLevelRowsSide.slice(startIdx)) collect(row);
      const flatRows = this.#flattenRowsForBody(this.topLevelRows).rows;
      for (const row of flatRows) {
        if (footerSubtreeIds.has(row.id)) numFooterFlattened += 1;
      }
    }
    return { numFooter, numFooterFlattened };
  }

  #buildFlattened(): void {
    this.#flatIndexById.clear();
    for (const r of this.#flattenRowsForBody(this.topLevelRows).rows) {
      this.#flatIndexById.set(r.id, this.flattened.rows.length);
      this.flattened.rows.push(r);
      this.flattened.wireTypes.push(r.descriptor.id);
      if (!r.isSystemRow) this.flattened.nonSystemRows.push(r);
    }
  }

  #flattenRowsForBody(
    rows: readonly SheetRow.Joined.Handle[],
  ): JoinedFlattenResult {
    return this.#flattenRowsSideRows(
      nonnull(this.#rowsSheet, "SheetRowTree.flattenRowsForBody: rows sheet required"),
      rows,
    );
  }

  #flattenRowsSideRows(
    rowsSheet: Sheet,
    rows: readonly SheetRow.Joined.Handle[],
  ): JoinedFlattenResult {
    const view = this.#deriveBodyViewTreeRowsSide(rowsSheet, rows);
    const result = flattenRowsSideTree(view.topLevelRowsSide, {
      timescale: this.timescale,
      wireTypeOf: wireTypeOf,
      registries: rowsSheet.registries,
      aliasProjection: "leaf",
      childrenOf: view.childrenOfRowsSide,
    });
    // Projection rows are dropped from the view tree, so flatten
    // never visits them. Mark them subsumed explicitly so
    // downstream consumers (CellLayout walker, joined-row.subsumed
    // flag) skip them too.
    const subsumed = new Set(result.subsumed);
    for (const id of view.droppedProjectionIds) subsumed.add(id);
    return {
      rows: this.#toJoinedRows(result.rows),
      subsumed,
      rendersChildren: result.rendersChildren,
    };
  }

  /**
   * Build a body view tree (BodyViewTree.deriveSheet) over the
   * rows-side rows and expose its top-level + child-resolution as
   * the rows-side types that flatten consumes. Also returns the
   * set of projection-row ids dropped by the view tree, so
   * downstream consumers can mark them subsumed.
   */
  #deriveBodyViewTreeRowsSide(
    rowsSheet: Sheet,
    topLevelJoined: readonly SheetRow.Joined.Handle[],
  ): {
    topLevelRowsSide: ModelRow[];
    childrenOfRowsSide: (
      row: ModelRow,
    ) => readonly ModelRow[];
    /**
     * Complete view-tree childMap (id → view children). Covers
     * every row reachable in the view tree, including hoisted
     * leaves whose entry is `[]` (they have no children in the
     * view tree, even though their authored row has children).
     * Exposed so `deriveBodyViewTreeJoined` can build the
     * joined-side map from the same source instead of re-
     * walking the authored tree.
     */
    rowsSideChildMap: ReadonlyMap<RowLocalId, readonly ModelRow[]>;
    droppedProjectionIds: ReadonlySet<RowLocalId>;
  } {
    const topLevelRowsSide = this.#toRowsSideRows(rowsSheet, topLevelJoined);
    const views = inlineTransparentDataAliases(
      BodyViewTree.deriveSheet(topLevelRowsSide),
    );

    // Map row.id → its view-tree children (rows-side). Walk views
    // recursively. Also collect dropped projection ids so the
    // caller can mark them subsumed.
    const childMap = new Map<RowLocalId, readonly ModelRow[]>();
    const droppedProjectionIds = new Set<RowLocalId>();
    const collect = (view: BodyViewTree.View<ModelRow>): void => {
      const kids = view.children();
      childMap.set(view.row.id, kids.map((k) => k.row));
      for (const kid of kids) collect(kid);
    };
    for (const v of views) collect(v);

    // The view tree drops every projection container; collect
    // their ids by walking the authored tree and matching any
    // projection that didn't get reached above.
    const reached = new Set<RowLocalId>(childMap.keys());
    const findDropped = (item: ModelRow): void => {
      if (
        item instanceof InertRow &&
        item.descriptor.kind === "projection" &&
        !reached.has(item.id)
      ) {
        droppedProjectionIds.add(item.id);
      }
      for (const child of item.children) findDropped(child);
    };
    for (const r of topLevelRowsSide) findDropped(r);

    return {
      topLevelRowsSide: views.map((v) => v.row),
      childrenOfRowsSide: (row) => childMap.get(row.id) ?? row.children,
      rowsSideChildMap: childMap,
      droppedProjectionIds,
    };
  }

  /**
   * Joined-row view of the body view tree. Used by `computeLayout`
   * to feed `CellLayout.Map.make` the same shape that `flatten`
   * consumes — flat output and layout offsets agree because both
   * walks share the same input.
   *
   * The view tree itself is derived in rows-side form (the
   * `BodyViewTree.deriveSheet` operates on `ModelRow`);
   * this helper translates each row id back to its joined-row
   * counterpart for `CellLayout.Map.make`. The returned
   * `childrenOfJoined` matches `CellLayout.Map.ChildrenOf<RowLocalId>`
   * structurally — `SheetRow.Joined.Handle` satisfies the
   * `Row<RowLocalId>` shape that interface requires.
   */
  #deriveBodyViewTreeJoined(): {
    topLevelJoined: SheetRow.Joined.Handle[];
    childrenById: (id: RowLocalId) => SheetRow.Joined.Handle[];
    childrenOfJoined: CellLayout.Map.ChildrenOf<RowLocalId>;
  } {
    const topLevelJoined = this.children(RootNode);
    if (this.#rowsSheet == null) {
      // No rows-side sheet → no view-tree derivation possible;
      // fall back to authored-tree children.
      const fallback = (id: RowLocalId): SheetRow.Joined.Handle[] =>
        this.byId.get(id)?.children() ?? [];
      return {
        topLevelJoined,
        childrenById: fallback,
        // The layout walks rows handed out by `byId`, so looking the
        // row back up by id is the typed form of `row.children()`.
        childrenOfJoined: (row) => fallback(row.id),
      };
    }
    const view = this.#deriveBodyViewTreeRowsSide(this.#rowsSheet, topLevelJoined);

    // Translate a list of rows-side rows to their joined-row
    // counterparts, dropping any that aren't reachable in `byId`
    // (defensive — should be 1:1).
    const toJoined = (
      kidsRowsSide: readonly ModelRow[],
    ): SheetRow.Joined.Handle[] => {
      const out: SheetRow.Joined.Handle[] = [];
      for (const k of kidsRowsSide) {
        const j = this.byId.get(k.id);
        if (j !== undefined) out.push(j);
      }
      return out;
    };

    // Translate the rowsSide view's complete childMap (including
    // hoisted leaves whose entry is `[]`). Walking the authored
    // tree here would miss hoisted rows and let
    // `viewChildren(timeline[row].id)` fall through to authored
    // children (= `[calendar-list]`).
    const childMap = new Map<RowLocalId, SheetRow.Joined.Handle[]>();
    for (const [id, kidsRowsSide] of view.rowsSideChildMap) {
      childMap.set(id, toJoined(kidsRowsSide));
    }

    const childrenById = (id: RowLocalId): SheetRow.Joined.Handle[] =>
      childMap.get(id) ?? this.byId.get(id)?.children() ?? [];

    return {
      topLevelJoined: toJoined(view.topLevelRowsSide),
      childrenById,
      childrenOfJoined: (row) => childrenById(row.id),
    };
  }

  #toRowsSideRows(
    rowsSheet: Sheet,
    rows: readonly SheetRow.Joined.Handle[],
  ): ModelRow[] {
    const out: ModelRow[] = [];
    for (const row of rows) {
      const rowsRow = rowsSheet.peekRow(row.id);
      if (rowsRow == null) {
        throw new Error(
          `SheetRowTree.toRowsSideRows: missing rows-side row ${row.id}`,
        );
      }
      out.push(rowsRow);
    }
    return out;
  }

  #toJoinedRows(
    rows: readonly ModelRow[],
  ): SheetRow.Joined.Handle[] {
    const out: SheetRow.Joined.Handle[] = [];
    for (const row of rows) {
      const joined = this.byId.get(row.id);
      if (joined === undefined) {
        throw new Error(
          `SheetRowTree.toJoinedRows: missing joined row ${row.id}`,
        );
      }
      out.push(joined);
    }
    return out;
  }

  #markRowsSideSubsumed(): void {
    const result = this.#flattenRowsForBody(this.topLevelRows);
    // Projection hoists can paint a descendant independently of its authored alias.
    // Only non-painted descendants remain owned by their parent's renderer.
    const painted = new Set(result.rows.map(row => row.id));
    for (const id of result.subsumed) {
      if (painted.has(id)) continue;
      const row = this.byId.get(id);
      if (row !== undefined) row.joined.subsumed = true;
    }
  }

  #currentRendersChildrenState(): ReadonlyMap<RowLocalId, boolean> {
    if (this.#rowsSheet != null) {
      return this.#flattenRowsForBody(this.topLevelRows).rendersChildren;
    }
    const state = new Map<RowLocalId, boolean>();
    for (const row of this.rows) {
      state.set(row.id, row.rendersChildren);
    }
    return state;
  }
}

/**
 * Types and values that go with `SheetRowTree`: `CellWiring`, `PositionRow`,
 * `nextBodyAppendPosition`, `Config`.
 */
export namespace SheetRowTree {
  /**
   * The cell accessor a tree binds its rows' cells through, opaque: the SDK
   * builds it from the client's accessor, and nothing outside the SDK reads
   * it back or builds one.
   */
  export class CellWiring {
    #accessor: Option<CellAccessor> = null;

    private constructor() {}

    static {
      wiringOf = (accessor) => {
        const wiring = new CellWiring();
        wiring.#accessor = accessor;
        return wiring;
      };
      accessorOfWiring = (wiring) => wiring.#accessor;
    }
  }

  /** Minimal top-level-row shape needed to compute a body-append position. */
  export type PositionRow = {
    readonly position: Rational;
    readonly isSystemRow: boolean;
    readonly surface: RowSection;
  };

  /**
   * Position at which to append a new top-level *body* row: after the last
   * non-system `body`-surface row (header/footer and system rows are
   * skipped), or `Rational.first()` when the body is empty. Anchoring here
   * rather than off the trailing rendered row (`lastRow`, which may be a
   * footer/system row) keeps a new row landing after body content.
   */
  export function nextBodyAppendPosition(tree: {
    readonly topLevelRows: readonly PositionRow[];
  }): Rational {
    let lastBodyRow: Option<PositionRow> = null;
    for (const row of tree.topLevelRows) {
      if (row.isSystemRow || row.surface !== "body") continue;
      lastBodyRow = row;
    }
    return lastBodyRow ? Rational.next(lastBodyRow.position) : Rational.first();
  }

  export type Config = {
    systemRowIds: Set<RowLocalId>;
    footerRowIds: Set<RowLocalId>;
    idRegistry: RowLocalId.Registry;
    timescale: Timescale;
    host: SheetHost;
    invalidation: InvalidationSink;
    /**
     * The client's cell accessor, as the tree's opaque cell wiring (the SDK
     * builds it); `null` for a tree without one (a headless test tree).
     */
    cells: Option<CellWiring>;
    /**
     * The owning sheet's lifecycle (`SheetHandle.joinedTree`): a row held
     * past the sheet's disposal refuses `cell` / `events` with
     * `SheetHandle.DisposedError`, and its active cell subscriptions fail.
     * Absent for a tree no sheet owns.
     */
    sheetLifecycle?: SheetLifecycle;
    api: TypedApi<Network.Routes>;
    editable?: boolean;
    rowHeightPreferences?: RowHeightPreferences;
    /**
     * Canonical rows-side `Sheet` from `SheetHandle.model`. The
     * tree shape (parents, children, root order) reads from
     * `Sheet.parentOf` / `childrenOf`; `SheetRow.Joined.Handle.variant` resolves
     * the typed tento/tento-lona-js/sheets subclass instance by `RowLocalId`.
     */
    rowsSheet: Sheet;
    /**
     * Client-scoped events table. Forwarded to
     * `SheetRow.Joined.Handle` so `eventOps` can route writes via
     * `getRowStateByKey(variant.lookupKey)` when `rowHandle` is
     * null (virtual leaf with no SDK-side handle).
     */
    eventsTable?: EventsTable;
    /**
     * Plugin descriptor overrides (`deps.rows.descriptors`). Applied
     * to bridged rows-side virtual rows, which never pass through
     * `SheetRow.Registry.set`.
     */
    descriptors?: SheetRow.DescriptorOverrides;
    /**
     * Mirrors a joined row's own-field change into the client's other
     * readers (`RowMirror`; `SheetHandle.joinedTree` passes the client's).
     * Absent for a tree no client registry holds.
     */
    rowMirror?: RowMirror;
    /**
     * The client's row-field ledger and transport (`RowFieldWrites`) the
     * tree's rows write their own fields through (`SheetHandle.joinedTree`
     * passes the client's). Absent for a tree no client holds: its rows
     * write through their rows-side sheet's own ledger
     * (`RowFieldWrites.ofSheet`, one per tree).
     */
    rowWrites?: RowFieldWrites;
  };
}

/**
 * The cell key a renderer reads through: when it is bound to data
 * (`RendererRow.isBound`), its data row's own cell key (`cellAccessKey` over
 * the data row's id and address) — none when the renderer is its own data row
 * (it carries the key, so its own handle is the data's); otherwise its
 * parent's, as before.
 */
function rendererDataKey(row: ModelRow, parentId: RowLocalId | RootNode): Option<RowNetworkKey> {
  if (row instanceof RendererRow && row.isBound) {
    if (row.dataRow === row) return null;
    return RowNetworkKey.fromWire(cellAccessKey(row.dataRow.id.toString(), row.dataAddress));
  }
  return parentId === RootNode ? null : RowNetworkKey.of(parentId);
}

function cellAccessRowKey(row: SheetRow): string {
  return cellAccessKey(row.id.toString(), row.lookupKey);
}

/**
 * Synthesize a legacy joined row for rows-side virtual children that
 * have no matching network-row entry in `inner.metadata`.
 */
function bridgeRowsSideRowToSheetRow(rowsRow: ModelRow): SheetRow {
  const id = rowsRow.id;
  const meta = rowsRow.sheetReads.rowMeta(id);
  const wireType = wireTypeOf(rowsRow);
  const input: SheetRow.Input = {
    type: wireType,
    ownerId: UserId.DETACHED,
    timeScale: "custom",
    // An authored row: a field the rows-side row does not set is absent
    // (`mergeRowsSideMetadata` reads absent and a virtual row's `null` alike).
    ...(rowsRow.label != null ? { label: rowsRow.label } : {}),
    ...(rowsRow.lookupKey != null ? { lookupKey: rowsRow.lookupKey } : {}),
    ...(rowsRow.heightPx != null ? { heightPx: rowsRow.heightPx } : {}),
    ...(meta.attributes != null
      ? { attributes: { ...meta.attributes } }
      : {}),
    kind: id.key.isVirtual() ? "parent-stored" : "stored",
  };
  return SheetRow.from(id, input, null);
}

function mergeRowsSideMetadata(
  sheetRow: SheetRow,
  rowsRow: ModelRow,
): void {
  const meta = rowsRow.sheetReads.rowMeta(rowsRow.id);
  let resetCache = false;
  if (sheetRow.raw.lookupKey === undefined && rowsRow.lookupKey !== undefined) {
    sheetRow.raw.lookupKey = rowsRow.lookupKey;
  }
  // A virtual row's network half (`RowNode`) states `null` for the label it
  // inherits from its rows-side spec.
  const inheritsLabel = sheetRow.raw.label === undefined ||
    (sheetRow.raw.label === null && rowsRow.id.key.isVirtual());
  if (inheritsLabel && rowsRow.label !== undefined) {
    sheetRow.raw.label = rowsRow.label;
  }
  if (sheetRow.raw.heightPx === undefined && rowsRow.heightPx !== undefined) {
    sheetRow.raw.heightPx = rowsRow.heightPx;
  }
  if (sheetRow.raw.attributes === undefined && meta.attributes != null) {
    sheetRow.raw.attributes = { ...meta.attributes };
    resetCache = true;
  }
  if (resetCache) sheetRow.resetCache();
}

function rowsByIdString(
  inner: RowNode.Tree<typeof Resolved>,
): Map<string, SheetRow> {
  const rows = new Map<string, SheetRow>();
  for (const [id, row] of inner.metadata.iter()) {
    rows.set(id.toString(), row);
  }
  return rows;
}

function positionsById(
  inner: RowNode.Tree<typeof Resolved>,
): Map<RowLocalId, Rational> {
  const positions = new Map<RowLocalId, Rational>();
  const visit = (node: Node): void => {
    positions.set(node.id, node.position);
    for (const child of node.children) visit(child);
  };
  for (const node of inner.nodes) visit(node);
  return positions;
}

function orderRowsSideSiblings(
  ids: readonly RowLocalId[],
  positions: ReadonlyMap<RowLocalId, Rational>,
): RowLocalId[] {
  const insertionIndex = new Map<RowLocalId, number>();
  ids.forEach((id, i) => insertionIndex.set(id, i));
  return [...ids].sort((a, b) => {
    const aPosition = positions.get(a) ?? null;
    const bPosition = positions.get(b) ?? null;
    if (aPosition !== null && bPosition !== null) {
      return Rational.compare(aPosition, bPosition);
    }
    // No resolvable position: PRESERVE the rows-side insertion order, which is
    // the authored/template order. Never REVERSE it — a reversing fallback
    // silently flips the whole sheet. The ephemeral→server promote that used to
    // expose this (an id-namespace split between the reused rows-side sheet and
    // the rebuilt ordered tree) now re-keys the rows-side sheet to the server
    // ids (`SheetModelCache.rekeyRows`), so positions resolve directly; this
    // stays as defense-in-depth for any residual unresolved row.
    return (insertionIndex.get(a) ?? 0) - (insertionIndex.get(b) ?? 0);
  });
}
